import { formatDistanceToNow } from 'date-fns';
import {
  BarChart3,
  Brain,
  Building2,
  Calendar,
  Globe,
  MapPin,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTranslateOnFly } from '../../../hooks/useTranslateOnFly';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { joinUniqueParts } from '../../../lib/format';
import { colors } from '../../../theme/colors';
import { ProfileDivider, ProfileInfoRow } from './ProfileInfoRow';

export interface CompanyAnalysisData {
  companyScore?: number;
  companyCapacity?: 'High' | 'Medium' | 'Low';
  companyAiAnalysis?: string;
  companyReasonScore?: {
    industryFit?: number;
    companySizeGrowth?: number;
    digitalPresence?: number;
    marketPosition?: number;
  };
}

export interface CompanyProfileData {
  companyName?: string;
  companyUrl?: string;
  websiteUrl?: string;
  industry?: string;
  employeeCount?: number;
  followerCount?: number;
  tagline?: string;
  description?: string;
  headquarter?: {
    country?: string;
    city?: string;
    geographicArea?: string;
    postalCode?: string;
    line1?: string;
  };
  foundedOn?: { year?: number; month?: number };
  specialities?: string[];
  scrapedAt?: string;
}

interface CompanyProfileCardProps {
  data: CompanyProfileData;
  analysis?: CompanyAnalysisData | null;
}

function formatHeadquarter(hq: CompanyProfileData['headquarter']): string | null {
  if (!hq) return null;
  const joined = joinUniqueParts([hq.city, hq.geographicArea, hq.country]);
  return joined.length > 0 ? joined : null;
}

function getScoreColor(score: number): string {
  if (score >= 7) return colors.status.success.main;
  if (score >= 4) return colors.status.warning.main;
  return colors.status.error.main;
}

function CompanyScoreGauge({ score }: Readonly<{ score: number }>) {
  const { t } = useTranslation();
  const percentage = Math.round((score / 10) * 100);
  const scoreColor = getScoreColor(score);

  const getScoreBg = () => {
    if (score >= 7) return colors.status.success.bg;
    if (score >= 4) return colors.status.warning.bg;
    return colors.status.error.bg;
  };

  const getScoreLabel = () => {
    if (score >= 7) return t('leads.companyProfile.highPotential');
    if (score >= 4) return t('leads.companyProfile.mediumPotential');
    return t('leads.companyProfile.lowPotential');
  };

  return (
    <Box backgroundColor={getScoreBg()} borderRadius="lg" padding="md" alignItems="center">
      <Text variant="h3" color={scoreColor} style={{ fontWeight: '800' }}>
        {score}/10
      </Text>
      <Text variant="caption" color={scoreColor} style={{ fontWeight: '600', marginTop: 2 }}>
        {getScoreLabel()}
      </Text>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ width: '100%', height: 4, marginTop: 8 }}
      >
        <Box
          backgroundColor={scoreColor}
          borderRadius="full"
          style={{ width: `${percentage}%`, height: 4 }}
        />
      </Box>
    </Box>
  );
}

function CompanyScoreBar({
  label,
  score,
  icon,
}: Readonly<{ label: string; score: number; icon: ReactNode }>) {
  const percentage = Math.round((score / 10) * 100);

  return (
    <VStack spacing="xxs">
      <HStack spacing="sm" align="center" justify="between">
        <HStack spacing="xs" align="center" style={{ flex: 1 }}>
          {icon}
          <Text variant="caption" color={colors.text.secondary} numberOfLines={1}>
            {label}
          </Text>
        </HStack>
        <Text variant="caption" color={colors.text.primary} style={{ fontWeight: '600' }}>
          {score}/10
        </Text>
      </HStack>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ width: '100%', height: 3 }}
      >
        <Box
          backgroundColor={getScoreColor(score)}
          borderRadius="full"
          style={{ width: `${percentage}%`, height: 3 }}
        />
      </Box>
    </VStack>
  );
}

export function CompanyProfileCard({ data, analysis }: Readonly<CompanyProfileCardProps>) {
  const { t, language } = useTranslation();
  const { aiAnalysis: translatedCompanyAnalysis, description: translatedDescription } =
    useTranslateOnFly({
      aiAnalysis: analysis?.companyAiAnalysis,
      description: data.description,
    });

  const hqString = formatHeadquarter(data.headquarter);
  const hasContent =
    data.industry || data.employeeCount || data.followerCount || data.websiteUrl || hqString;

  if (!hasContent && !data.companyName) return null;

  const hasScoreBreakdown =
    analysis?.companyReasonScore && Object.keys(analysis.companyReasonScore).length > 0;

  return (
    <VStack spacing="md">
      <HStack spacing="sm" align="center">
        <Building2 size={18} color={colors.accent.blue[400]} strokeWidth={2} />
        <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
          {t('leads.companyProfile.title')}
        </Text>
      </HStack>

      {data.companyName && (
        <Text variant="body" color={colors.text.primary} style={{ fontWeight: '600' }}>
          {data.companyName}
        </Text>
      )}

      {data.tagline && (
        <Text variant="bodySmall" color={colors.text.secondary} style={{ lineHeight: 20 }}>
          {data.tagline}
        </Text>
      )}

      {analysis?.companyScore != null && <CompanyScoreGauge score={analysis.companyScore} />}

      {analysis?.companyCapacity && (
        <>
          <ProfileDivider />
          <HStack spacing="sm" align="center" justify="between">
            <HStack spacing="xs" align="center">
              <TrendingUp size={14} color={colors.accent.amber[400]} strokeWidth={2} />
              <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.companyProfile.capacity')}
              </Text>
            </HStack>
            <Badge
              variant="subtle"
              color={
                analysis.companyCapacity === 'High'
                  ? 'green'
                  : analysis.companyCapacity === 'Medium'
                    ? 'amber'
                    : 'red'
              }
              size="md"
            >
              {t(`leads.companyProfile.capacity${analysis.companyCapacity}`)}
            </Badge>
          </HStack>
        </>
      )}

      {data.industry && (
        <ProfileInfoRow
          icon={<Building2 size={14} color={colors.accent.blue[400]} strokeWidth={1.5} />}
          label={t('leads.companyProfile.industry')}
          value={data.industry}
        />
      )}

      {data.employeeCount != null && (
        <ProfileInfoRow
          icon={<Users size={14} color={colors.primary[400]} strokeWidth={1.5} />}
          label={t('leads.companyProfile.employees')}
          value={data.employeeCount.toLocaleString()}
        />
      )}

      {data.followerCount != null && (
        <ProfileInfoRow
          icon={<Users size={14} color={colors.accent.purple[400]} strokeWidth={1.5} />}
          label={t('leads.companyProfile.followers')}
          value={data.followerCount.toLocaleString()}
        />
      )}

      {data.websiteUrl && (
        <ProfileInfoRow
          icon={<Globe size={14} color={colors.accent.amber[400]} strokeWidth={1.5} />}
          label={t('leads.companyProfile.website')}
          value={data.websiteUrl}
        />
      )}

      {hqString && (
        <ProfileInfoRow
          icon={<MapPin size={14} color={colors.status.success.main} strokeWidth={1.5} />}
          label={t('leads.companyProfile.headquarters')}
          value={hqString}
        />
      )}

      {data.foundedOn?.year && (
        <ProfileInfoRow
          icon={<Calendar size={14} color={colors.text.tertiary} strokeWidth={1.5} />}
          label={t('leads.companyProfile.founded')}
          value={String(data.foundedOn.year)}
        />
      )}

      {data.specialities && data.specialities.length > 0 && (
        <>
          <ProfileDivider />
          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {t('leads.companyProfile.specialities')}
            </Text>
            <HStack spacing="xs" style={{ flexWrap: 'wrap' }}>
              {data.specialities.map((s) => (
                <Badge key={s} variant="subtle" color="blue" size="sm">
                  {s}
                </Badge>
              ))}
            </HStack>
          </VStack>
        </>
      )}

      {hasScoreBreakdown && (
        <>
          <ProfileDivider />
          <VStack spacing="sm">
            <HStack spacing="xs" align="center">
              <BarChart3 size={14} color={colors.primary[400]} strokeWidth={2} />
              <Text variant="bodySmall" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.companyProfile.scoreBreakdown')}
              </Text>
            </HStack>
            {analysis?.companyReasonScore?.industryFit != null && (
              <CompanyScoreBar
                label={t('leads.companyProfile.industryFit')}
                score={analysis.companyReasonScore.industryFit}
                icon={<Target size={12} color={colors.accent.blue[400]} strokeWidth={1.5} />}
              />
            )}
            {analysis?.companyReasonScore?.companySizeGrowth != null && (
              <CompanyScoreBar
                label={t('leads.companyProfile.companySizeGrowth')}
                score={analysis.companyReasonScore.companySizeGrowth}
                icon={<Users size={12} color={colors.primary[400]} strokeWidth={1.5} />}
              />
            )}
            {analysis?.companyReasonScore?.digitalPresence != null && (
              <CompanyScoreBar
                label={t('leads.companyProfile.digitalPresence')}
                score={analysis.companyReasonScore.digitalPresence}
                icon={<Globe size={12} color={colors.accent.amber[400]} strokeWidth={1.5} />}
              />
            )}
            {analysis?.companyReasonScore?.marketPosition != null && (
              <CompanyScoreBar
                label={t('leads.companyProfile.marketPosition')}
                score={analysis.companyReasonScore.marketPosition}
                icon={<TrendingUp size={12} color={colors.status.success.main} strokeWidth={1.5} />}
              />
            )}
          </VStack>
        </>
      )}

      {analysis?.companyAiAnalysis && (
        <>
          <ProfileDivider />
          <VStack spacing="sm">
            <HStack spacing="xs" align="center">
              <Brain size={14} color={colors.accent.purple[400]} strokeWidth={2} />
              <Text variant="bodySmall" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                {t('leads.companyProfile.aiAnalysis')}
              </Text>
            </HStack>
            <Box backgroundColor={colors.surface.row} borderRadius="md" padding="sm">
              <Text variant="caption" color={colors.text.secondary} style={{ lineHeight: 18 }}>
                {translatedCompanyAnalysis ?? analysis.companyAiAnalysis}
              </Text>
            </Box>
          </VStack>
        </>
      )}

      {data.description && (
        <>
          <ProfileDivider />
          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {t('leads.companyProfile.description')}
            </Text>
            <Box backgroundColor={colors.surface.row} borderRadius="md" padding="sm">
              <Text variant="caption" color={colors.text.secondary} style={{ lineHeight: 18 }}>
                {translatedDescription ?? data.description}
              </Text>
            </Box>
          </VStack>
        </>
      )}

      {data.scrapedAt && (
        <>
          <ProfileDivider />
          <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
            {t('leads.companyProfile.scrapedLabel')}{' '}
            {formatDistanceToNow(new Date(data.scrapedAt), {
              addSuffix: true,
              locale: DATE_LOCALES[language],
            })}
          </Text>
        </>
      )}
    </VStack>
  );
}
